import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const DialogueContext = createContext();

export const DialogueProvider = ({ children, getAuthHeader, user }) => {
  const [currentDialogueId, setCurrentDialogueId] = useState(null);
  const [currentDialogueName, setCurrentDialogueName] = useState(null);
  const [dialogueList, setDialogueList] = useState([]);
  const [loadingDialogues, setLoadingDialogues] = useState(false);
  const [creatingDialogue, setCreatingDialogue] = useState(false);

  /**
   * Load list of dialogues for the user
   */
  const loadDialogues = useCallback(async (limit = 50, offset = 0) => {
    if (!user) return;

    setLoadingDialogues(true);
    try {
      const res = await fetch(`/api/dialogues?limit=${limit}&offset=${offset}`, {
        headers: { Authorization: getAuthHeader() }
      });

      if (!res.ok) {
        console.error('Failed to load dialogues:', res.statusText);
        return;
      }

      const data = await res.json();
      if (data.success) {
        setDialogueList(data.dialogues);
        console.log(`[Dialogue] Loaded ${data.dialogues.length} dialogues`);
      }
    } catch (err) {
      console.error('Error loading dialogues:', err);
    } finally {
      setLoadingDialogues(false);
    }
  }, [user, getAuthHeader]);

  /**
   * Create a new dialogue
   */
  const createNewDialogue = useCallback(async () => {
    if (!user) return null;

    setCreatingDialogue(true);
    try {
      const res = await fetch('/api/dialogues', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        }
      });

      if (!res.ok) {
        console.error('Failed to create dialogue:', res.statusText);
        return null;
      }

      const data = await res.json();
      if (data.success) {
        const newDialogue = data.dialogue;

        // Switch to new dialogue
        setCurrentDialogueId(newDialogue.id);
        setCurrentDialogueName(newDialogue.name);

        // Store in localStorage
        localStorage.setItem('lastDialogueId', newDialogue.id);

        console.log(`[Dialogue] Created new dialogue ${newDialogue.id}`);
        return newDialogue;
      }
      return null;
    } catch (err) {
      console.error('Error creating dialogue:', err);
      return null;
    } finally {
      setCreatingDialogue(false);
    }
  }, [user, getAuthHeader]);

  /**
   * Switch to a different dialogue
   * @param {string} dialogueId - Dialogue UUID to switch to
   * @param {function} onMessagesLoaded - Callback with loaded messages
   */
  const switchDialogue = useCallback(async (dialogueId, onMessagesLoaded) => {
    if (!user || !dialogueId) return null;

    try {
      const res = await fetch(`/api/dialogues/${dialogueId}/messages`, {
        headers: { Authorization: getAuthHeader() }
      });

      if (!res.ok) {
        console.error('Failed to load dialogue:', res.statusText);
        return null;
      }

      const data = await res.json();
      if (data.success) {
        // Update current dialogue
        setCurrentDialogueId(dialogueId);
        setCurrentDialogueName(data.dialogue.name);

        // Store in localStorage
        localStorage.setItem('lastDialogueId', dialogueId);

        // Call callback with messages and transactions
        if (onMessagesLoaded) {
          onMessagesLoaded(data.messages, data.transactions);
        }

        console.log(`[Dialogue] Switched to dialogue ${dialogueId}: ${data.messages.length} messages, ${data.transactions.length} transactions`);
        return data;
      }
      return null;
    } catch (err) {
      console.error('Error switching dialogue:', err);
      return null;
    }
  }, [user, getAuthHeader]);

  /**
   * Restore a dialogue on page load
   * @param {string} dialogueId - Dialogue UUID to restore
   * @param {function} onMessagesLoaded - Callback with loaded messages
   */
  const restoreDialogue = useCallback(async (dialogueId, onMessagesLoaded) => {
    if (!user || !dialogueId) return null;

    try {
      const res = await fetch(`/api/dialogues/${dialogueId}/messages`, {
        headers: { Authorization: getAuthHeader() }
      });

      if (!res.ok) {
        console.error('Failed to restore dialogue:', res.statusText);
        // If dialogue not found, clear localStorage
        if (res.status === 404) {
          localStorage.removeItem('lastDialogueId');
        }
        return null;
      }

      const data = await res.json();
      if (data.success) {
        // Update current dialogue
        setCurrentDialogueId(dialogueId);
        setCurrentDialogueName(data.dialogue.name);

        // Call callback with messages and transactions
        if (onMessagesLoaded) {
          onMessagesLoaded(data.messages, data.transactions);
        }

        console.log(`[Dialogue] Restored dialogue ${dialogueId}: ${data.messages.length} messages, ${data.transactions.length} transactions`);
        return data;
      }
      return null;
    } catch (err) {
      console.error('Error restoring dialogue:', err);
      return null;
    }
  }, [user, getAuthHeader]);

  /**
   * Rename a dialogue
   * @param {string} dialogueId - Dialogue UUID
   * @param {string} newName - New dialogue name
   */
  const renameDialogue = useCallback(async (dialogueId, newName) => {
    if (!user || !dialogueId || !newName) return false;

    try {
      const res = await fetch(`/api/dialogues/${dialogueId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify({ name: newName })
      });

      if (!res.ok) {
        console.error('Failed to rename dialogue:', res.statusText);
        return false;
      }

      const data = await res.json();
      if (data.success) {
        // Update in dialogue list
        setDialogueList(prev =>
          prev.map(d => d.id === dialogueId ? { ...d, name: newName } : d)
        );

        // Update current dialogue name if active
        if (currentDialogueId === dialogueId) {
          setCurrentDialogueName(newName);
        }

        console.log(`[Dialogue] Renamed dialogue ${dialogueId} to "${newName}"`);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error renaming dialogue:', err);
      return false;
    }
  }, [user, getAuthHeader, currentDialogueId]);

  /**
   * Archive a dialogue (soft delete)
   * @param {string} dialogueId - Dialogue UUID
   */
  const archiveDialogue = useCallback(async (dialogueId) => {
    if (!user || !dialogueId) return false;

    try {
      const res = await fetch(`/api/dialogues/${dialogueId}`, {
        method: 'DELETE',
        headers: { Authorization: getAuthHeader() }
      });

      if (!res.ok) {
        console.error('Failed to archive dialogue:', res.statusText);
        return false;
      }

      const data = await res.json();
      if (data.success) {
        // Remove from dialogue list
        setDialogueList(prev => prev.filter(d => d.id !== dialogueId));

        // If archived dialogue was active, clear current dialogue
        if (currentDialogueId === dialogueId) {
          setCurrentDialogueId(null);
          setCurrentDialogueName(null);
          localStorage.removeItem('lastDialogueId');
        }

        console.log(`[Dialogue] Archived dialogue ${dialogueId}`);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error archiving dialogue:', err);
      return false;
    }
  }, [user, getAuthHeader, currentDialogueId]);

  /**
   * Initialize dialogue system on mount
   */
  useEffect(() => {
    if (user) {
      loadDialogues();
    }
  }, [user, loadDialogues]);

  const value = {
    // State
    currentDialogueId,
    currentDialogueName,
    dialogueList,
    loadingDialogues,
    creatingDialogue,

    // Actions
    setCurrentDialogueId,
    setCurrentDialogueName,
    loadDialogues,
    createNewDialogue,
    switchDialogue,
    restoreDialogue,
    renameDialogue,
    archiveDialogue
  };

  return (
    <DialogueContext.Provider value={value}>
      {children}
    </DialogueContext.Provider>
  );
};

export const useDialogue = () => {
  const context = useContext(DialogueContext);
  if (!context) {
    // Return safe defaults instead of throwing during unmount/remount transitions
    return {
      currentDialogueId: null,
      currentDialogueName: null,
      dialogueList: [],
      loadingDialogues: false,
      creatingDialogue: false,
      setCurrentDialogueId: () => {},
      setCurrentDialogueName: () => {},
      loadDialogues: async () => {},
      createNewDialogue: async () => null,
      switchDialogue: async () => null,
      restoreDialogue: async () => null,
      renameDialogue: async () => false,
      archiveDialogue: async () => false
    };
  }
  return context;
};
